/**
 * Run-instance status stream client (AC-1364).
 *
 * Wraps event-source-polyfill so the SSE connection can carry the standard
 * Authorization header. Native browser EventSource cannot send headers, which
 * is why we use the polyfill.
 *
 * The wrapper exposes a tiny callback API mirroring the existing
 * pollRunResults shape, so the workflow store can swap transports without
 * leaking implementation details to the editor.
 */
import { EventSourcePolyfill } from 'event-source-polyfill';
import type {
  Event as PolyfillEvent,
  MessageEvent as PolyfillMessageEvent,
} from 'event-source-polyfill';
import type { RunStatus } from '../types';

export interface RunStatusEvent {
  type: 'snapshot' | 'status';
  run_instance_id: number;
  status: RunStatus;
  current_step_id?: number | null;
  old_status?: RunStatus | null;
  timestamp?: string;
}

export interface RunStatusStreamOptions {
  /** Initial status snapshot or every status transition. */
  onStatus?: (event: RunStatusEvent) => void;
  /**
   * Stream closed cleanly because the run reached a terminal state. The
   * caller should NOT reconnect from here; the run is done.
   */
  onTerminal?: (status: RunStatus) => void;
  /**
   * Transport-level error. The stream will auto-reconnect with backoff
   * unless `stop()` was called. After several consecutive failures the
   * caller is expected to fall back to polling.
   */
  onError?: (error: Error, retryCount: number) => void;
}

const TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set<RunStatus>([
  'completed',
  'failed',
  'stopped',
]);

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

/**
 * Open an SSE stream for a run instance. Returns a handle with `stop()` so
 * the caller can tear it down on unmount.
 *
 * Reconnect policy: exponential backoff up to 30s. Reconnects do not reset
 * `retryCount` until a successful event arrives, so the caller can decide
 * to fall back to polling after N failures.
 */
export function openRunStatusStream(
  runInstanceId: number | string,
  getAuthToken: () => string | null,
  options: RunStatusStreamOptions = {},
): { stop: () => void } {
  const url = `/api/v1/orchestrations/run-instances/${runInstanceId}/events`;
  let stopped = false;
  let source: EventSourcePolyfill | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let retryCount = 0;
  let sawTerminal = false;

  const cleanup = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (source) {
      try {
        source.close();
      } catch {
        /* ignore */
      }
      source = null;
    }
  };

  const scheduleReconnect = () => {
    if (stopped || sawTerminal) return;
    // Use (retryCount - 1) so the first reconnect lands at RECONNECT_BASE_MS
    // (1s) rather than 2s. retryCount has already been incremented by the
    // error handler at this point.
    const exponent = Math.max(0, retryCount - 1);
    const delay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, exponent),
      RECONNECT_MAX_MS,
    );
    reconnectTimer = setTimeout(connect, delay);
  };

  const connect = () => {
    if (stopped) return;
    cleanup();

    const token = getAuthToken();
    if (!token) {
      // Without a token the connection will 401 immediately; surface as error
      // so the caller can fall back to polling, then stop trying.
      options.onError?.(new Error('No auth token available for SSE'), retryCount);
      stopped = true;
      return;
    }

    source = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${token}` },
      // The backend emits a heartbeat every 15s; allow 35s of silence before
      // declaring the connection dead so a single missed heartbeat doesn't
      // tear down a healthy stream.
      heartbeatTimeout: 35000,
    });

    // The polyfill ships its own Event/MessageEvent types that shadow the
    // DOM ones; import them explicitly so addEventListener resolves to the
    // string overload without a type mismatch.
    source.addEventListener('status', (e: PolyfillEvent) => {
      const messageEvent = e as PolyfillMessageEvent;
      try {
        const payload = JSON.parse(messageEvent.data) as RunStatusEvent;
        retryCount = 0; // any successful event resets the backoff
        options.onStatus?.(payload);
        if (payload.status && TERMINAL_STATUSES.has(payload.status)) {
          sawTerminal = true;
          options.onTerminal?.(payload.status);
          stopped = true;
          cleanup();
        }
      } catch (err) {
        options.onError?.(
          err instanceof Error ? err : new Error('Malformed SSE payload'),
          retryCount,
        );
      }
    });

    source.addEventListener('error', (event: PolyfillEvent) => {
      // The polyfill stuffs the HTTP status onto the event for non-200 reads.
      // We don't differentiate here; the caller's onError + reconnect logic
      // handles it. (401 specifically: by the time the polyfill retries, the
      // axios interceptor on a parallel REST call may have refreshed the
      // token; new connections will pick it up via getAuthToken.)
      const status = (event as unknown as { status?: number }).status;
      const message = status
        ? `SSE error (HTTP ${status})`
        : 'SSE connection error';
      retryCount += 1;
      options.onError?.(new Error(message), retryCount);
      cleanup();
      scheduleReconnect();
    });
  };

  connect();

  return {
    stop: () => {
      stopped = true;
      cleanup();
    },
  };
}
